import { createHash, randomUUID } from "crypto";
import { sql } from "drizzle-orm";
import { BETTER_AUTH_OAUTH_STATE_TTL_MS } from "./auth-oauth-invite-reservation.js";

type ExecuteDb =
  | {
      execute: (query: unknown) => Promise<unknown>;
    }
  | null
  | undefined;

export type OAuthSignUpHandoff =
  | { ok: true; token: string; expiresAt: Date }
  | { ok: false; error: string };

const OAUTH_SIGNUP_HANDOFF_TTL_MS = BETTER_AUTH_OAUTH_STATE_TTL_MS + 5 * 60 * 1000;

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

function hashHandoffToken(token: string): string {
  return createHash("sha256").update(token).digest("hex");
}

function normalizeProvider(provider: unknown): string {
  return typeof provider === "string" && provider.trim() ? provider.trim().toLowerCase() : "unknown";
}

export async function releaseExpiredOAuthSignUpHandoffs(db: ExecuteDb): Promise<void> {
  if (!db) return;
  await db.execute(sql`
    UPDATE _gencow_oauth_signup_handoffs
    SET status = 'released',
        released_at = NOW()
    WHERE status = 'pending'
      AND expires_at <= NOW()
  `);
}

export async function createOAuthSignUpHandoff(
  db: ExecuteDb,
  input: { provider: unknown },
): Promise<OAuthSignUpHandoff> {
  if (!db) return { ok: false, error: "OAuth 가입 상태를 생성할 수 없습니다." };

  await releaseExpiredOAuthSignUpHandoffs(db);
  const token = randomUUID();
  const expiresAt = new Date(Date.now() + OAUTH_SIGNUP_HANDOFF_TTL_MS);

  try {
    await db.execute(sql`
      INSERT INTO _gencow_oauth_signup_handoffs (
        token_hash,
        provider_id,
        expires_at
      )
      VALUES (
        ${hashHandoffToken(token)},
        ${normalizeProvider(input.provider)},
        ${expiresAt}
      )
    `);
    return { ok: true, token, expiresAt };
  } catch (error) {
    console.error("[auth] OAuth signup handoff error:", error);
    return { ok: false, error: "OAuth 가입 상태를 생성할 수 없습니다." };
  }
}

export async function releaseOAuthSignUpHandoff(db: ExecuteDb, token: unknown): Promise<void> {
  if (!db || typeof token !== "string" || !token.trim()) return;
  await db.execute(sql`
    UPDATE _gencow_oauth_signup_handoffs
    SET status = 'released',
        released_at = NOW()
    WHERE token_hash = ${hashHandoffToken(token)}
      AND status = 'pending'
  `);
}

export async function consumeOAuthSignUpHandoff(
  db: ExecuteDb,
  token: unknown,
  user: { id: string; email: string },
): Promise<boolean> {
  if (!db || typeof token !== "string" || !token.trim()) return false;

  await releaseExpiredOAuthSignUpHandoffs(db);
  const consumed = await db.execute(sql`
    UPDATE _gencow_oauth_signup_handoffs
    SET status = 'completed',
        user_id = ${user.id},
        user_email = ${user.email},
        completed_at = NOW()
    WHERE token_hash = ${hashHandoffToken(token)}
      AND status = 'pending'
      AND expires_at > NOW()
    RETURNING token_hash
  `);
  return rowsFromResult(consumed).length > 0;
}
